import React, { createContext, useState, useContext, useEffect } from 'react';
import { testAccounts } from '../data/users';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('podcastUser');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = (email, password) => {
    const foundUser = testAccounts.find(
      account => account.email === email && account.password === password
    );

    if (foundUser) {
      const userWithoutPassword = { ...foundUser };
      delete userWithoutPassword.password;
      setUser(userWithoutPassword);
      localStorage.setItem('podcastUser', JSON.stringify(userWithoutPassword));
      return { success: true };
    }

    return { success: false, error: 'Invalid email or password' };
  };

  const signup = (email, password, name) => {
    const existingUser = testAccounts.find(account => account.email === email);

    if (existingUser) {
      return { success: false, error: 'Email already exists' };
    }

    const newUser = {
      id: Date.now().toString(),
      email,
      name,
      avatar: '/images/avatars/default.jpg',
      subscriptions: [],
      favorites: [],
      listening: null
    };

    setUser(newUser);
    localStorage.setItem('podcastUser', JSON.stringify(newUser));
    return { success: true };
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('podcastUser');
  };

  const updateUserListening = (podcastId, episodeId, progress) => {
    if (user) {
      const updatedUser = {
        ...user,
        listening: {
          currentPodcast: podcastId,
          currentEpisode: episodeId,
          progress
        }
      };
      setUser(updatedUser);
      localStorage.setItem('podcastUser', JSON.stringify(updatedUser));
    }
  };

  const toggleSubscription = (podcastId) => {
    if (user) {
      const subscriptions = user.subscriptions || [];
      const isSubscribed = subscriptions.includes(podcastId);

      const updatedUser = {
        ...user,
        subscriptions: isSubscribed
          ? subscriptions.filter(id => id !== podcastId)
          : [...subscriptions, podcastId]
      };

      setUser(updatedUser);
      localStorage.setItem('podcastUser', JSON.stringify(updatedUser));
    }
  };

  const toggleFavorite = (episodeId) => {
    if (user) {
      const favorites = user.favorites || [];
      const isFavorite = favorites.includes(episodeId);

      const updatedUser = {
        ...user,
        favorites: isFavorite
          ? favorites.filter(id => id !== episodeId)
          : [...favorites, episodeId]
      };

      setUser(updatedUser);
      localStorage.setItem('podcastUser', JSON.stringify(updatedUser));
    }
  };

  const value = {
    user,
    login,
    signup,
    logout,
    updateUserListening,
    toggleSubscription,
    toggleFavorite,
    isAuthenticated: !!user,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
